// =============================================
// QuestionsTable.jsx
// Auto-detect math fields + math-safe truncation
// + Post-UTME visual classification (SAFE)
// NO logic removed, NO behavior compromised
// =============================================

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  IconButton,
  Chip,
  Tooltip,
  Typography,
  Box,
  Stack,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";

import { MathJax } from "better-react-mathjax";

// =============================================
// 🔍 Detect LaTeX / Math content
// =============================================
const hasMath = (text = "") =>
  /\$|\\(frac|dfrac|sqrt|log|sin|cos|tan)|\^|_/.test(text);


// =============================================
// Component
// =============================================
export default function QuestionsTable({
  questions,
  truncated, // kept for plain-text truncation
  selected,
  selectAll,
  onToggleSelectAll,
  onToggleOne,
  onEdit,
  onDelete,
  onDuplicate,
}) {
  // =============================================
  // 📐 Visual-only truncation (SAFE for LaTeX)
  // =============================================
  const clampStyle = {
    maxWidth: 420,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
  };

  // =============================================
  // 🧠 Smart renderer (math vs plain text)
  // =============================================
  const renderCell = (text, maxChars = 80, variant = "body2") => {
    if (!text) return null;

    // ✅ Math → render fully, truncate visually only
    if (hasMath(text)) {
      return (
        <Box sx={clampStyle}>
          <MathJax dynamic>
            <Typography variant={variant}>{text}</Typography>
          </MathJax>
        </Box>
      );
    }

    // ✅ Plain text → normal string truncation
    return (
      <Typography variant={variant}>
        {truncated(text, maxChars)}
      </Typography>
    );
  };

  // =============================================
  // RENDER
  // =============================================
  return (
    <Table stickyHeader size="small">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              checked={selectAll}
              indeterminate={
                selected.length > 0 && selected.length < questions.length
              }
              onChange={(e) => onToggleSelectAll(e.target.checked)}
            />
          </TableCell>

          <TableCell>ID</TableCell>
          <TableCell>Question</TableCell>
          <TableCell>Options</TableCell>
          <TableCell>Correct</TableCell>
          <TableCell>Difficulty</TableCell>
          <TableCell>Category</TableCell>
          <TableCell>Image</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {questions.map((q) => (
          <TableRow key={q.id} hover>
            {/* ================= SELECT ================= */}
            <TableCell padding="checkbox">
              <Checkbox
                checked={selected.includes(q.id)}
                onChange={(e) => onToggleOne(q.id, e.target.checked)}
              />
            </TableCell>

            {/* ================= ID ================= */}
            <TableCell>{q.id}</TableCell>

            {/* ================= QUESTION ================= */}
            <TableCell>
              {renderCell(q.question, 100, "body2")}
            </TableCell>

            {/* ================= OPTIONS ================= */}
            <TableCell>
              <Box sx={{ maxWidth: 420 }}>
                <Typography variant="caption" component="div">
                  <b>A:</b> {renderCell(q.option_a, 30, "caption")}
                  <br />
                  <b>B:</b> {renderCell(q.option_b, 30, "caption")}
                  <br />
                  <b>C:</b> {renderCell(q.option_c, 30, "caption")}
                  <br />
                  <b>D:</b> {renderCell(q.option_d, 30, "caption")}
                </Typography>
              </Box>
            </TableCell>

            {/* ================= CORRECT ================= */}
            <TableCell>
              <Chip label={q.correct_option} color="primary" size="small" />
            </TableCell>

            {/* ================= DIFFICULTY ================= */}
            <TableCell>
              <Chip label={q.difficulty} size="small" variant="outlined" />
            </TableCell>

            {/* ================= CATEGORY (NEW, SAFE) ================= */}
            <TableCell>
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {q.exam_style === "SCREENING" && (
                  <Chip
                    label="POST-UTME"
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}

                {q.bucket_code && (
                  <Chip
                    label={q.bucket_code}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Stack>
            </TableCell>

            {/* ================= IMAGE ================= */}
            <TableCell>
              {q.stem_image_url ? (
                <Tooltip title="View image">
                  <a
                    href={q.stem_image_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ImageIcon fontSize="small" />
                  </a>
                </Tooltip>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  None
                </Typography>
              )}
            </TableCell>

            {/* ================= ACTIONS ================= */}
            <TableCell align="right">
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit(q)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>


              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDelete(q)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
